import { z } from 'zod'
import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { ApiError, createApiClient } from '../../services/api/client'
import {
  createPatientRequestSchema,
  createPatientResponseSchema,
  patientsResponseSchema,
  type CreatePatientRequest,
  type CreatePatientResponse,
  type PatientsResponse,
} from './patients-contracts'

const patientErrorSchema = z.object({ message: z.string().max(200) })

export type PatientsRepository = {
  list: (tenantId: string, options?: PatientsQuery) => Promise<PatientsResponse>
  get: (
    tenantId: string,
    patientAuthorityId: string
  ) => Promise<PatientsResponse>
  create: (
    tenantId: string,
    patient: CreatePatientRequest
  ) => Promise<CreatePatientResponse>
}

export type PatientsQuery = {
  query?: string
  filter?: 'all' | 'new' | 'existing' | 'upcoming' | 'recent'
  cursor?: string | null
  pageSize?: number
}

export function createPatientsRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): PatientsRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  const endpoint = (tenantId: string) =>
    `/dashboard/clinics/${encodeURIComponent(tenantId)}/patients`
  const listEndpoint = (tenantId: string, options: PatientsQuery = {}) => {
    const params = new URLSearchParams()
    params.set('pageSize', String(options.pageSize ?? 50))
    if (options.query?.trim()) params.set('query', options.query.trim())
    if (options.filter && options.filter !== 'all')
      params.set('filter', options.filter)
    if (options.cursor) params.set('cursor', options.cursor)
    return `${endpoint(tenantId)}?${params}`
  }
  return {
    list: (tenantId, options) =>
      client.request(listEndpoint(tenantId, options), {
        method: 'GET',
        cache: 'no-store',
        responseSchema: patientsResponseSchema,
      }),
    get: (tenantId, patientAuthorityId) => {
      const params = new URLSearchParams({ patientAuthorityId, pageSize: '1' })
      return client.request(`${endpoint(tenantId)}?${params}`, {
        method: 'GET',
        cache: 'no-store',
        responseSchema: patientsResponseSchema,
      })
    },
    create: (tenantId, patient) =>
      client.request(endpoint(tenantId), {
        method: 'POST',
        body: createPatientRequestSchema.parse(patient),
        responseSchema: createPatientResponseSchema,
        // The endpoint's own {message} (e.g. "Patient match requires review")
        // reaches the dialog as ApiError.reason; the contract is unchanged.
        errorSchema: patientErrorSchema,
      }),
  }
}

export function patientReadError(error: unknown): 'forbidden' | 'transport' {
  return error instanceof ApiError &&
    (error.status === 401 || error.status === 403)
    ? 'forbidden'
    : 'transport'
}
